import React from "react";
import { Card, message, Col, Row, Avatar, Tooltip, Typography } from "antd";

import StatsIcons from "../../../../componets/StatsIcons";
import { Button, ButtonDestroy, ButtonEdit, OptionsButton } from "../../../../componets/Buttons";
import Meta from "../../../../componets/Meta";

import './_ListItem.scss';
import { setBanId, setEditId, UserInterface } from "../../../../store/mine_craft/usersSlice";
import IconText from "../../../../componets/IconText";
import {
  AppstoreAddOutlined,
  DownloadOutlined,
  LikeOutlined,
  MessageOutlined, StopOutlined,
  UserOutlined,
  UserSwitchOutlined
} from "@ant-design/icons";
import { EntityId } from "@reduxjs/toolkit";
import { destroyUser } from "../../../../actions/mine_craft/usersActions";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { useNavigate } from "react-router-dom";
import { selectAddonEntities } from "../../../../store/mine_craft/addonsSlice";
import { formatterToUnits } from "../../../../helpers/NumberHelper";
import { formattedDate } from "../../../../helpers/DateHelpers";
import OptionalLink from "../../../../componets/OptionalLink";
import Title from "./_Title";
import { doHighlight } from "../../../../helpers/TextHelpers";
import usePermissions from "../../../../hooks/usePermissions";
const { Paragraph } = Typography;

const _ListItem = ({ user, term }: { user: UserInterface, term: string; }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const addons = useAppSelector(selectAddonEntities);
  const formattedCreatedAt = formattedDate(user.created_at);
  const { write_minecraft } = usePermissions();

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  function onClickDestroy(id: EntityId) {
    handleDestroy(id).catch(console.error);
  }

  function onClickBan(id: EntityId) {
    dispatch(setBanId(id));
  }

  async function handleDestroy(id: any) {
    const resultAction = await dispatch(destroyUser(id));

    if (destroyUser.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      setTimeout(() => navigate(0), 1000);
    }

    if (destroyUser.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  const renderAddons = (ids: number[] | undefined) => {
    if(!ids || ids.length === 0) {
      return null;
    }

    const addonsLeft = formatterToUnits(user.addons_count - ids.length);

    return (
      <Avatar.Group>
        {ids.map((id: number) => {
          const addon = addons[id];

          return (
            <OptionalLink key={id} link={addon?.url}>
              <Tooltip title={addon?.title} placement="top">
                <Avatar src={addon?.image_url} shape='square' size={75} />
              </Tooltip>
            </OptionalLink>
          );
        })}

        <OptionalLink key='addons-left' link={user?.url}>
          <Tooltip title={`Count: ${user.addons_count}`} placement="top">
            <Avatar className='v-card-inline-image--addons-left' shape='square' size={75} style={{ backgroundColor: '#f56a00' }}>{`+${addonsLeft}`}</Avatar>
          </Tooltip>
        </OptionalLink>
      </Avatar.Group>
    );
  }

  return (
    <Card className='v-card-inline-image'>
      <div className='o-body'>
        <Row>
          <Col span={4} className='v-card-inline-image--left'>
            <Meta
              url={user.url}
              size='small'
              shape='square'
              avatar={user.avatar_url}
              fallback={UserOutlined}
              badge={user.is_online ? 'green' : 'red'}
              title={<Title name={doHighlight(user.name, term)} bannedUntil={user.banned_until} mutedUntil={user.muted_until} />}
            />

            <div className='v-card-inline-image--dates'>
              <div>
                <small>{`Seen: ${formattedDate(user.current_sign_in_at).fromNow}`}</small>
              </div>

              <div>
                <small>Register: {formattedCreatedAt.date} <small>{formattedCreatedAt.time}</small></small>
              </div>
            </div>
          </Col>

          <Col span={10} className='v-card-inline-image--left'>
            <Paragraph ellipsis={{ rows: 4, expandable: true }}>
              {doHighlight(user.about_me, term)}
            </Paragraph>
          </Col>

          <Col span={10} className='v-card-inline-image--right'>
            <div className='v-card-inline-image--addons-list'>
              {addons && renderAddons(user?.last_addons)}
            </div>
          </Col>
        </Row>
      </div>

      <div className='o-footer'>
        <StatsIcons
          icons={[
            <IconText tooltip='likes' icon={LikeOutlined} text={user?.likes_count} />,
            <IconText tooltip='downloads' icon={DownloadOutlined} text={user?.downloads_count} />,

            <IconText tooltip='addons' icon={AppstoreAddOutlined} text={user?.addons_count} />,
            <IconText tooltip='comments' icon={MessageOutlined} text={user?.comments_count} />,

            <IconText tooltip='following' icon={UserOutlined} text={user?.following_counter} />,
            <IconText tooltip='followers' icon={UserSwitchOutlined} text={user?.followers_counter} />
          ]}
        />

        {
          write_minecraft &&
            <OptionsButton
              key='settings'
              options={[
                <Button key='button-ban' onClick={() => onClickBan(user.id)} title='Block' icon={StopOutlined} />,
                <ButtonEdit key={`edit-${user.id}`} onClick={() => onClickEdit(user.id)} />,
                <ButtonDestroy key={`destroy-${user.id}`} onClick={() => onClickDestroy(user.id)} />
              ]}
            />
        }
      </div>
    </Card>
  )
}

export default _ListItem;
